/** Survives client navigations where `history.state` is dropped before `/player/live/…`. */

import { RAIL_TITLE } from "@utils/constants";

export const SEARCH_PLAYBACK_ATTRIBUTION_KEY = "FE_SEARCH_CONTENT_PLAY_FALLBACK";

/**
 * Persist search→detail rail context keyed by live content id (URL param).
 * @param {object} payload
 * @param {string} payload.contentId
 */
export const setSearchPlaybackAttribution = (payload) => {
    try {
        if (typeof sessionStorage === "undefined" || !payload?.contentId) return;
        sessionStorage.setItem(
            SEARCH_PLAYBACK_ATTRIBUTION_KEY,
            JSON.stringify({
                ...payload,
                contentId: String(payload.contentId),
                storedAt: Date.now(),
            })
        );
    } catch (e) {
        /* quota / privacy mode */
    }
};

export const peekSearchPlaybackAttribution = (contentId) => {
    try {
        if (typeof sessionStorage === "undefined" || contentId === undefined || contentId === null) {
            return null;
        }
        const raw = sessionStorage.getItem(SEARCH_PLAYBACK_ATTRIBUTION_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (String(data?.contentId) !== String(contentId)) return null;
        return data;
    } catch (e) {
        return null;
    }
};

/**
 * Drop session fallback only when navigation state clearly is not search.
 * If `state` is missing or ambiguous, keep storage so PLAYER can still match by content id + referrer.
 */
export const clearSearchPlaybackAttributionIfKnownNonSearch = (state) => {
    if (
        state == null ||
        state.source === "Search" ||
        String(state.source || "").toUpperCase() === "SEARCH" ||
        state.railTitle === RAIL_TITLE.SEARCH_SUGGESTIONS ||
        state.railTitle === RAIL_TITLE.SEARCH_RESULT ||
        (typeof state.railTitle === "string" && state.railTitle.startsWith("SEARCH-"))
    ) {
        return;
    }
    if (state.source === undefined && state.railTitle === undefined) {
        return;
    }
    try {
        if (typeof sessionStorage !== "undefined") {
            sessionStorage.removeItem(SEARCH_PLAYBACK_ATTRIBUTION_KEY);
        }
    } catch (e) {
        /* ignore */
    }
};

/** SPA-safe hint when `$referrer` still points at `/search?…`. */
export const isDocumentReferrerFromSearchRoute = () => {
    try {
        if (typeof document === "undefined" || !document?.referrer) return false;
        const u = new URL(document.referrer);
        const path = `${u.pathname || ""}${u.search || ""}`.toLowerCase();
        return path.includes("/search");
    } catch (e) {
        return false;
    }
};

/** Android parity: grid with keyword → SEARCH-RESULT; suggestions/header rails → SEARCH-SUGGESTIONS. */
export const inferSearchRailTitleFromReferrer = () => {
    try {
        if (typeof document === "undefined" || !document?.referrer) {
            return RAIL_TITLE.SEARCH_SUGGESTIONS;
        }
        const u = new URL(document.referrer);
        if (!`${u.pathname || ""}`.toLowerCase().includes("search")) {
            return RAIL_TITLE.SEARCH_SUGGESTIONS;
        }
        const q = u.searchParams.get("q") ?? "";
        const hasQuery = typeof q === "string" && q.trim().length > 0;
        return hasQuery ? RAIL_TITLE.SEARCH_RESULT : RAIL_TITLE.SEARCH_SUGGESTIONS;
    } catch (e) {
        return RAIL_TITLE.SEARCH_SUGGESTIONS;
    }
};
